import { useEffect, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import { ErrorBoundary } from './ErrorBoundary';
import logger from '../../utils/logger';

export const IframePDFViewer = ({
  file,
  onLoadSuccess,
  onLoadError,
  loading,
  error,
  className,
  style,
  zoom = 100,
  page = 1,
  pointerEvents = 'auto'
}) => {
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [baseBlobUrl, setBaseBlobUrl] = useState(null); // Store base URL without hash params

  // Compute the full URL with page parameter
  const getFullUrl = (baseUrl, pageNum) => {
    if (!baseUrl) return null;
    return `${baseUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=150&page=${pageNum}`;
  };

  // Fetch PDF as blob and create blob URL for inline viewing
  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    setHasLoaded(false);

    // Clean up previous blob URL
    if (baseBlobUrl) {
      URL.revokeObjectURL(baseBlobUrl);
      setBaseBlobUrl(null);
    }
    const fetchPDF = async () => {
      try {
        logger.log('[IframePDFViewer] Fetching PDF:', file);
        const response = await fetch(file);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();

        // Ensure it's treated as a PDF
        const pdfBlob = new Blob([blob], {
          type: 'application/pdf'
        });
        const url = URL.createObjectURL(pdfBlob);

        // Store base URL without hash parameters
        logger.log('[IframePDFViewer] Created base blob URL:', url);
        setBaseBlobUrl(url);
      } catch (err) {
        console.error('[IframePDFViewer] Error fetching PDF:', err);
        const error = err instanceof Error ? err : new Error('Failed to fetch PDF');
        setLoadError(error);
        setIsLoading(false);
        if (onLoadError) {
          onLoadError(error);
        }
      }
    };
    fetchPDF();

    // Cleanup function
    return () => {
      if (baseBlobUrl) {
        URL.revokeObjectURL(baseBlobUrl);
      }
    };
  }, [file]);

  // Update iframe URL when page changes - use useEffect as backup to ensure navigation
  // Defensive: validate page number is within bounds
  useEffect(() => {
    // Defensive: ensure page is valid positive integer
    const safePage = Math.max(1, Math.floor(page || 1));
    if (baseBlobUrl && iframeRef.current && safePage >= 1 && hasLoaded) {
      const newUrl = getFullUrl(baseBlobUrl, safePage);
      
      // Extract current page from iframe src to avoid unnecessary updates
      const currentSrc = iframeRef.current.src;
      const currentPageMatch = currentSrc.match(/[#&]page=(\d+)/);
      const currentPage = currentPageMatch ? parseInt(currentPageMatch[1], 10) : null;
      
      // Only update if the page actually changed
      if (currentPage !== safePage) {
        // Force navigation by updating src
        iframeRef.current.src = newUrl;
        logger.log(`[IframePDFViewer] Navigating to page ${safePage}`);
      }
    }
  }, [page, baseBlobUrl, hasLoaded]);
  const handleLoad = async () => {
    logger.log('[IframePDFViewer] PDF loaded in iframe');
    setIsLoading(false);
    setHasLoaded(true);

    // Try to get actual page count from PDF
    let pageCount = 1; // Default fallback
    
    try {
      // Try using PDF.js if available
      if (typeof window !== 'undefined' && window.pdfjsLib && baseBlobUrl) {
        const loadingTask = window.pdfjsLib.getDocument(baseBlobUrl);
        const pdf = await loadingTask.promise;
        pageCount = pdf.numPages;
        logger.log(`[IframePDFViewer] Detected ${pageCount} pages using PDF.js`);
      } else {
        // Fallback: try to parse the blob
        try {
          const response = await fetch(baseBlobUrl);
          const blob = await response.blob();
          const arrayBuffer = await blob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const text = new TextDecoder('latin1').decode(uint8Array);
          
          // Look for /Count in the document catalog
          const countMatch = text.match(/\/Count\s+(\d+)/);
          if (countMatch) {
            // Defensive: validate parsed count is positive integer
            const count = parseInt(countMatch[1], 10);
            if (!isNaN(count) && count > 0) {
              pageCount = count;
              logger.log(`[IframePDFViewer] Detected ${pageCount} pages from PDF structure`);
            }
          }
        } catch (parseError) {
          logger.warn('[IframePDFViewer] Could not parse PDF for page count:', parseError);
        }
      }
    } catch (error) {
      logger.warn('[IframePDFViewer] Could not get page count:', error);
    }

    if (onLoadSuccess) {
      onLoadSuccess({
        numPages: pageCount
      });
    }
  };
  const handleError = () => {
    console.error('[IframePDFViewer] Error in iframe load');
    const error = new Error('Failed to load PDF in iframe');
    setLoadError(error);
    setIsLoading(false);
    if (onLoadError) {
      onLoadError(error);
    }
  };
  if (loadError) {
    return error ? <>{error}</> : <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f2f2f2',
      padding: '20px'
    }}>
        <Typography sx={{
        fontFamily: 'Roboto, sans-serif',
        fontSize: '14px',
        color: '#d32f2f',
        textAlign: 'center'
      }}>
          Error loading PDF: {loadError.message}
        </Typography>
      </div>;
  }
  return (
    <ErrorBoundary
      fallback={
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f2f2f2',
          padding: '20px'
        }}>
          <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            color: '#d32f2f',
            textAlign: 'center'
          }}>
            Error loading PDF viewer. Please try again.
          </Typography>
        </div>
      }
    >
      <div className={className} style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        ...style
      }}>
        {(isLoading || !baseBlobUrl) && (loading ? <>{loading}</> : <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f2f2f2',
          zIndex: 10
        }}>
          <Typography sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            color: '#4a5565'
          }}>
            Loading PDF...
          </Typography>
        </div>)}
        {baseBlobUrl && <iframe 
          key={baseBlobUrl}
          ref={iframeRef} 
          src={getFullUrl(baseBlobUrl, page)} 
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            imageRendering: 'crisp-edges',
            pointerEvents: pointerEvents
          }} 
          title="PDF Viewer" 
          onLoad={handleLoad} 
          onError={handleError} 
        />}
      </div>
    </ErrorBoundary>
  );
};